import React, { useCallback, useState, useMemo } from 'react';
import { BaseModal } from '@app/components/common/BaseModal/BaseModal';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { updateLoginUserByIdAsync } from '@app/store/slices/userSlice';
import { notificationController } from '@app/controllers/notificationController';
import { useTranslation } from 'react-i18next';
import { updateGlobalLoading } from '@app/store/slices/GlobalLoadingSlice';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { BankOutlined, CheckCircleFilled } from '@ant-design/icons';

const StyledModal = styled(BaseModal)`
  & .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 600;
`;

const FirmCard = styled(BaseCard)<{ $isSelected: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  height: 100%;
  border-radius: 12px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }

  ${(props) =>
    props.$isSelected &&
    css`
      border-color: var(--primary-color);
      background-color: rgba(var(--primary-rgb-color), 0.04);
      box-shadow: 0 2px 8px rgba(var(--primary-rgb-color), 0.15);
    `}
`;

const FirmIcon = styled.div`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 8px;
`;

const FirmName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 4px;
  word-break: break-word;
`;

const SelectedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 500;
`;

interface FirmSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FirmSelectorModal: React.FC<FirmSelectorModalProps> = ({ isOpen, onClose }) => {
  const user = useAppSelector((state) => state.user.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const defaultFirmId = user?.DefualtFirmId;

  const firms = useMemo(() => user?.AllowedFirms || [], [user?.AllowedFirms]);

  const handleSelectFirm = useCallback(
    (firmId: number) => {
      setSelectedId(firmId);
      const values = {
        DefualtFirmId: firmId,
      };
      dispatch(updateGlobalLoading(true));
      dispatch(updateLoginUserByIdAsync({ id: user?.Id || 0, data: values }))
        .unwrap()
        .then((e) => {
          if (e) {
            notificationController.success({ message: t('common.success') });
            dispatch(updateGlobalLoading(false));
            onClose();
            navigate('/');
          } else {
            dispatch(updateGlobalLoading(false));
            onClose();
            navigate('/');
            notificationController.success({ message: t('failed to process client request') });
          }
        })
        .catch((err) => {
          dispatch(updateGlobalLoading(false));
          window.location.reload();
          notificationController.error({ message: err });
        });
    },
    [t, dispatch, user?.Id, navigate, onClose],
  );

  return (
    <StyledModal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="80vw"
      title={
        <ModalTitle>
          <BankOutlined />
          {t('Select Firm')}
        </ModalTitle>
      }
      destroyOnClose
      centered
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {firms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light-color)' }}>
          {t('No firms available')}
        </div>
      ) : (
        <BaseRow gutter={[16, 16]}>
          {firms.map((firm) => {
            const isSelected = firm.value === defaultFirmId;
            return (
              <BaseCol key={firm.value} xs={24} sm={12} md={8} lg={6}>
                <FirmCard
                  $isSelected={isSelected}
                  onClick={() => !isSelected && handleSelectFirm(firm.value)}
                  padding="16px 12px"
                >
                  <FirmIcon>
                    <BankOutlined />
                  </FirmIcon>
                  <FirmName>{firm.label}</FirmName>
                  {isSelected && (
                    <SelectedBadge>
                      <CheckCircleFilled /> Default
                    </SelectedBadge>
                  )}
                </FirmCard>
              </BaseCol>
            );
          })}
        </BaseRow>
      )}
    </StyledModal>
  );
};
